import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { delay, map, Observable, of } from 'rxjs';
import { HttpClient, HttpClientModule, HttpHeaders } from '@angular/common/http';
import { BadgeComponent, GlobalHomeTrackingEvent } from '../shared/index';
import { InfoCardComponent } from './info-card/info-card.component';
import { ProductCardComponent } from './product-card/product-card.component';
import { PromoCardComponent } from './promo-card/promo-card.component';
import { mockApiResponse } from './app.service';
import { Environment, PackageName, Product, RawApiResponse } from './types';
import { LoaderComponent } from './loader/loader.component';

declare global {
    interface Window {
        _falconAuth?: {
            token: string;
        };
    }
}

interface EnrichedProductAccess {
    packageName: PackageName;
    exporableProducts: Product[];
    accessibleProducts: Product[];
    hasConsumerResearchAccess: boolean;
    hasSocialMediaAccess: boolean;
    hasConsumerResearchAdminAccess: boolean;
}

const SMM_PRODUCTS = [
    Product.ADVERTISE,
    Product.AUDIENCE,
    Product.BENCHMARK,
    Product.ENGAGE,
    Product.LISTEN,
    Product.MEASURE,
    Product.PUBLISH,
];
@Component({
    standalone: true,
    selector: 'global-home-root',
    templateUrl: './app.component.html',
    styleUrls: ['../styles.scss', './app.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        HttpClientModule,
        InfoCardComponent,
        PromoCardComponent,
        BadgeComponent,
        ProductCardComponent,
        LoaderComponent,
    ],
})
export class AppComponent implements OnInit {
    @Input() set 'data-user-first-name'(value: string) {
        this.userFirstName = value;
    }

    @Input() set 'data-base-url'(value: string) {
        this.baseUrl = value;
    }

    @Input() set 'data-consumer-research-url'(value: string) {
        this.consumerResearchUrl = value;
    }

    @Input() set 'data-ci-api-token'(value: string) {
        this.ciAuthToken = value;
    }

    @Input() set 'data-environment'(value: string) {
        this.environment = value as Environment;
    }

    @Output() globalHomeEvent = new EventEmitter(); //Catch the event in Falcon/Brandwatch

    @Input() color!: string;

    title = 'global-home';

    userFirstName = '';

    baseUrl = '';

    private ciAuthToken = '';

    environment: Environment = Environment.PRODUCTION;

    consumerResearchUrl = '';

    enrichedProductAccess$!: Observable<EnrichedProductAccess>;

    constructor(private http: HttpClient) {}

    ngOnInit() {
        this.enrichedProductAccess$ = this.getCurrentAccess().pipe(
            map((response) => {
                const accessableProducts = response.products
                    .filter((product) => product.userHasAccess)
                    .map((product) => product.product);
                const exporableProducts = response.products
                    .filter((product) => !product.userHasAccess)
                    .map((product) => product.product);
                const hasConsumerResearchAccess = accessableProducts.includes(Product.CONSUMER_RESEARCH);
                const hasSocialMediaAccess = accessableProducts.some((product) => SMM_PRODUCTS.includes(product));
                const hasConsumerResearchAdminAccess = accessableProducts.includes(Product.BI_ADMIN);

                return {
                    packageName: response.packageName,
                    exporableProducts,
                    accessibleProducts: accessableProducts,
                    hasConsumerResearchAccess,
                    hasSocialMediaAccess,
                    hasConsumerResearchAdminAccess,
                };
            })
        );
    }

    dispatchEvent(name: string) {
        const event = new CustomEvent(GlobalHomeTrackingEvent.GLOBAL_HOME_ITEM_CLICKED, {
            bubbles: true,
            composed: true,
            detail: {
                _name: `globalHome-${name}-clicked`,
                _from: '',
                _package: '',
            },
        });
        this.globalHomeEvent.emit(event);
    }

    private getCurrentAccess(): Observable<RawApiResponse> {
        if (this.ciAuthToken) {
            return this.http.get<RawApiResponse>(`${this.baseUrl}/user/productAccess`, {
                headers: new HttpHeaders({
                    authorization: `bearer ${this.ciAuthToken}`,
                }),
            });
        } else if (window._falconAuth) {
            return this.http.get<RawApiResponse>(`${this.baseUrl}/suite/smm-user-product-access`, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${window._falconAuth.token}`,
                }),
            });
        }
        return of({ packageName: PackageName.ESSENTIALS, products: mockApiResponse.products }).pipe(delay(5000));
    }
}
